import { getAccessToken, ensureFreshAccessToken } from './authStore';
import { safeGetSession } from './supabase';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL as string | undefined;

if (!apiBaseUrl) {
  throw new Error(
    'Missing REACT_APP_API_BASE_URL. Set it to your API Gateway base URL, e.g. https://.../dev/api'
  );
}

export interface GoogleCalendarStatus {
  connected: boolean;
  configured: boolean;
  email?: string | null;
  calendarId?: string | null;
  connectedAt?: string | null;
  message?: string;
}

async function getJwtOrThrow(): Promise<string> {
  const token =
    (await ensureFreshAccessToken()) ||
    getAccessToken() ||
    (await safeGetSession(4000)).data.session?.access_token ||
    undefined;
  if (!token) {
    throw new Error('Votre session a expiré. Veuillez vous reconnecter et réessayer.');
  }
  return token;
}

async function jsonFetch<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const token = await getJwtOrThrow();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> || {}),
  };
  headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(input, { ...init, headers });

  if (!res.ok) {
    let message = `Request failed with status ${res.status}`;
    try {
      const body = await res.json();
      if (body?.message) message = body.message;
    } catch {
      // ignore
    }
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }

  return res.json() as Promise<T>;
}

/** Fire-and-forget: never throw to callers (sync must not block CRUD UX). */
function quiet(promise: Promise<unknown>): void {
  void promise.catch((err) => {
    console.warn('Google Calendar sync:', err);
  });
}

export const googleCalendarService = {
  async getStatus(): Promise<GoogleCalendarStatus> {
    return jsonFetch<GoogleCalendarStatus>(`${apiBaseUrl}/integrations/google/calendar/status`);
  },

  async getConnectUrl(): Promise<string> {
    const data = await jsonFetch<{ url: string }>(
      `${apiBaseUrl}/integrations/google/calendar/connect`
    );
    if (!data?.url) throw new Error('Missing connect URL');
    return data.url;
  },

  async disconnect(): Promise<void> {
    await jsonFetch(`${apiBaseUrl}/integrations/google/calendar/disconnect`, {
      method: 'POST',
      body: '{}',
    });
  },

  async syncEvent(eventId: string): Promise<void> {
    await jsonFetch(`${apiBaseUrl}/integrations/google/calendar/sync`, {
      method: 'POST',
      body: JSON.stringify({ eventId, action: 'upsert' }),
    });
  },

  async syncDelete(params: { eventId?: string; googleEventId?: string }): Promise<void> {
    await jsonFetch(`${apiBaseUrl}/integrations/google/calendar/sync`, {
      method: 'POST',
      body: JSON.stringify({ action: 'delete', ...params }),
    });
  },

  async syncAll(): Promise<{ synced?: number; failed?: number; skipped?: boolean }> {
    return jsonFetch(`${apiBaseUrl}/integrations/google/calendar/sync-all`, {
      method: 'POST',
      body: '{}',
    });
  },

  /** Non-blocking helpers used after local calendar CRUD */
  pushUpsert(eventId: string): void {
    if (!eventId || eventId.startsWith('temp-')) return;
    quiet(this.syncEvent(eventId));
  },

  pushDelete(params: { eventId?: string; googleEventId?: string | null | undefined }): void {
    if (!params.googleEventId && !params.eventId) return;
    quiet(
      this.syncDelete({
        eventId: params.eventId,
        googleEventId: params.googleEventId || undefined,
      })
    );
  },
};
